import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["__scopePromoModal"],
  _excluded2 = ["children"],
  _excluded3 = ["className", "children"],
  _excluded4 = ["__scopePromoModal", "asChild"],
  _excluded5 = ["__scopePromoModal", "asChild"],
  _excluded6 = ["size"],
  _excluded7 = ["__scopePromoModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { clsx } from 'clsx';
import { Button } from '../../../button';
import { createDialogScope } from '../../../headless/dialog/context';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { Slot } from '../../../internal/components/Slot';
import { createContextScope } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { ModalClose, ModalCloseButton, ModalContent, ModalDescription, ModalFooter, ModalRoot, ModalTitle, ModalTrigger } from '../Modal';
import * as promoModalStyles from './PromoModal.module.css';
import { jsx as _jsx } from "react/jsx-runtime";
var ROOT_NAME = 'PromoModal';
var BODY_NAME = 'PromoModalBody';
var CONTENT_NAME = 'PromoModalContent';
var ACTIONS_NAME = 'PromoModalActions';
var ACTION_NAME = 'PromoModalAction';
var ACTION_SECONDARY_NAME = 'PromoModalCancel';
var MEDIA_NAME = 'PromoModalMedia';

/**
 * создаем отдельный скоуп для PromoModal
 * чтобы избежать ошибок композиции контекстов с другими модальными окнами
 */

var _createContextScope = createContextScope(ROOT_NAME, [createDialogScope]),
  _createContextScope2 = _slicedToArray(_createContextScope, 2),
  createPromoModalContext = _createContextScope2[0],
  createPromoModalScope = _createContextScope2[1];
var useDialogScope = createDialogScope();
var _createPromoModalCont = createPromoModalContext(CONTENT_NAME),
  _createPromoModalCont2 = _slicedToArray(_createPromoModalCont, 2),
  PromoModalContentProvider = _createPromoModalCont2[0],
  usePromoModalContentContext = _createPromoModalCont2[1];

/**
 * HOC для оборачивания компонентов Modal в скоуп PromoModal
 * при необходимости расширения функциональности, можно создать отдельные компонеты, см PromoModalContent
 */
var withPromoModalScope = function withPromoModalScope(Component) {
  var WrappedComponent = function WrappedComponent(props) {
    var __scopePromoModal = props.__scopePromoModal,
      restProps = _objectWithoutProperties(props, _excluded);
    var ModalScope = useDialogScope(__scopePromoModal);
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, ModalScope), restProps));
  };
  WrappedComponent.displayName = "Promo(".concat(Component.displayName || Component.name, ")");
  return WrappedComponent;
};

/**
 * Копоненты Modal без изменения функционала, обернутые в скоуп PromoModal
 */
export var PromoModalRoot = withPromoModalScope(ModalRoot);
export var PromoModalTrigger = withPromoModalScope(ModalTrigger);
export var PromoModalTitle = withPromoModalScope(ModalTitle);
export var PromoModalDescription = withPromoModalScope(ModalDescription);
export var PromoModalClose = withPromoModalScope(ModalClose);
export var PromoModalCloseButton = withPromoModalScope(ModalCloseButton);
export var PromoModalFooter = withPromoModalScope(ModalFooter);

/**
 * Компонент для рендера тела модального окна
 */

export var PromoModalBody = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded2);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: promoModalStyles['body']
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
PromoModalBody.displayName = BODY_NAME;

/**
 * Компонент для рендера контейнера медиа-изображения в заголовке модального окна
 */

export var PromoModalMedia = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var className = _ref2.className,
    children = _ref2.children,
    rest = _objectWithoutProperties(_ref2, _excluded3);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: clsx(promoModalStyles['media'], className)
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
PromoModalMedia.displayName = MEDIA_NAME;

/**
 * Компонент обертка PromoModalActions
 * используется для правильного позиционирования кнопок действия
 * @example
 * <PromoModalActions>
 *  <PromoModalCancel>Отмена</PromoModalCancel>
 *  <PromoModalAction>Принять</PromoModalAction>
 * </PromoModalActions>
 */

export var PromoModalActions = /*#__PURE__*/React.forwardRef(function (_ref3, forwardedRef) {
  var children = _ref3.children;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: promoModalStyles['actions'],
    children: children
  });
});
PromoModalActions.displayName = ACTIONS_NAME;

/**
 * Button-like компонент для основного действия в модальном окне
 */

export var PromoModalAction = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopePromoModal = props.__scopePromoModal,
    asChild = props.asChild,
    rest = _objectWithoutProperties(props, _excluded4);
  var _usePromoModalContent = usePromoModalContentContext(ACTION_SECONDARY_NAME, __scopePromoModal),
    actionRef = _usePromoModalContent.actionRef;
  var ModalScope = useDialogScope(__scopePromoModal);
  var ref = useComposedRefs(forwardedRef, actionRef);
  var Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, ModalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L"
    }, rest), {}, {
      ref: ref
    }))
  }));
});
PromoModalAction.displayName = ACTION_NAME;

/**
 * Button-like компонент для доп действия в модальном окне
 */

export var PromoModalActionSecondary = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopePromoModal = props.__scopePromoModal,
    asChild = props.asChild,
    rest = _objectWithoutProperties(props, _excluded5);
  var ModalScope = useDialogScope(__scopePromoModal);
  var Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, ModalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L",
      theme: "fill_secondary"
    }, rest), {}, {
      ref: forwardedRef
    }))
  }));
});
PromoModalActionSecondary.displayName = ACTION_SECONDARY_NAME;
export var PromoModalContent = /*#__PURE__*/React.forwardRef(function (_ref4, forwardedRef) {
  var size = _ref4.size,
    props = _objectWithoutProperties(_ref4, _excluded6);
  var __scopePromoModal = props.__scopePromoModal,
    contentProps = _objectWithoutProperties(props, _excluded7);
  var modalScope = useDialogScope(__scopePromoModal);
  var actionRef = React.useRef(null);
  /**
   * При открытии фокусируемся на кнопке основного действия по умолчанию
   */
  var handleOnOpenAutoFocus = React.useMemo(function () {
    return composeEventHandlers(contentProps.onOpenAutoFocus, function (event) {
      var _actionRef$current;
      event.preventDefault();
      (_actionRef$current = actionRef.current) === null || _actionRef$current === void 0 || _actionRef$current.focus({
        preventScroll: true
      });
    });
  }, [contentProps.onOpenAutoFocus]);
  return /*#__PURE__*/_jsx(PromoModalContentProvider, {
    actionRef: actionRef,
    scope: __scopePromoModal,
    children: /*#__PURE__*/_jsx(ModalContent, _objectSpread(_objectSpread({
      size: size,
      portalProps: modalScope,
      overlayProps: modalScope,
      onOpenAutoFocus: handleOnOpenAutoFocus,
      ref: forwardedRef
    }, modalScope), contentProps))
  });
});
PromoModalContent.displayName = CONTENT_NAME;
export { createPromoModalScope };